import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Paperclip, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { APP_TYPES, COMPANY_SIZES } from "../data/content";
import { toast } from "../components/ui/sonner";

const DELIVERY = ["Standard", "Rush Hour", "Not Sure"];
const MODE = ["Remote", "On-site", "Not Sure"];
const BUDGETS = ["Under ₹1L", "₹1L - ₹3L", "₹3L - ₹6L", "₹6L - ₹12L", "₹12L+", "Not sure"];

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-semibold tracking-wider text-slate-400 mb-2">{label}</label>
    {children}
  </div>
);

export default function GetEstimate() {
  const [params] = useSearchParams();
  const { settings } = useSite();
  const initialDelivery = params.get("model") === "standard" ? "Standard" : params.get("model") === "rush" ? "Rush Hour" : "Not Sure";

  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "", country: "", city: "", company_size: "",
    application_type: "", business_problem: "", project_description: "", target_users: "",
    key_features: "", integrations: "", expected_users: "", delivery_preference: initialDelivery,
    mode_preference: "Not Sure", expected_timeline: "", estimated_budget: "",
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    for (const f of picked) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await api.post("/uploads", fd);
        setFiles((prev) => [...prev, res.data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...form, file_ids: files.map((f) => f.file_id) });
      track("estimate_submitted");
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Submission failed. Please try again or WhatsApp us your requirement.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-32" data-testid="estimate-success">
        <div className="container-x max-w-2xl text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 className="mt-8 font-heading text-4xl sm:text-5xl font-black tracking-tight text-white">REQUIREMENT RECEIVED.</h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Your requirement has been received. Our team will review the scope and contact you for requirement
            clarification and estimation.
          </p>
          <p className="mt-8 font-mono text-[11px] tracking-[0.3em] text-slate-600">BUILT2LAST.LIVE — BUILD SOFTWARE. LIVE.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="get-estimate-page">
      <div className="container-x max-w-4xl">
        <p className="section-label">GET AN ESTIMATE</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white">
          WHAT DO YOU WANT <span className="text-flame">TO BUILD?</span>
        </h1>
        <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
          Plain language is enough. We analyse your requirement, estimate development hours and get back to you with
          scope and a delivery model.
        </p>

        <form onSubmit={submit} className="mt-14 rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12 space-y-10" data-testid="estimate-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="FULL NAME *" testid="estimate-field-name"><Input required value={form.full_name} onChange={set("full_name")} data-testid="estimate-name" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="COMPANY NAME" testid="estimate-field-company"><Input value={form.company} onChange={set("company")} data-testid="estimate-company" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="EMAIL *" testid="estimate-field-email"><Input required type="email" value={form.email} onChange={set("email")} data-testid="estimate-email" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="PHONE" testid="estimate-field-phone"><Input value={form.phone} onChange={set("phone")} data-testid="estimate-phone" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="COUNTRY" testid="estimate-field-country"><Input value={form.country} onChange={set("country")} data-testid="estimate-country" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="CITY" testid="estimate-field-city"><Input value={form.city} onChange={set("city")} data-testid="estimate-city" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="COMPANY SIZE" testid="estimate-field-size">
              <Select value={form.company_size} onValueChange={set("company_size")}>
                <SelectTrigger data-testid="estimate-company-size" className="bg-navy-900/60 border-white/10 h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="APPLICATION TYPE *" testid="estimate-field-apptype">
              <Select value={form.application_type} onValueChange={set("application_type")} required>
                <SelectTrigger data-testid="estimate-app-type" className="bg-navy-900/60 border-white/10 h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{APP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="space-y-6">
            <Field label="BUSINESS PROBLEM *" testid="estimate-field-problem"><Textarea required rows={3} value={form.business_problem} onChange={set("business_problem")} data-testid="estimate-problem" placeholder="What operational or business problem should this software solve?" className="bg-navy-900/60 border-white/10" /></Field>
            <Field label="PROJECT DESCRIPTION *" testid="estimate-field-description"><Textarea required rows={4} value={form.project_description} onChange={set("project_description")} data-testid="estimate-description" placeholder="Describe the application you want." className="bg-navy-900/60 border-white/10" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="TARGET USERS" testid="estimate-field-users"><Input value={form.target_users} onChange={set("target_users")} data-testid="estimate-target-users" placeholder="Who will use it?" className="bg-navy-900/60 border-white/10 h-12" /></Field>
              <Field label="EXPECTED NUMBER OF USERS" testid="estimate-field-expected"><Input value={form.expected_users} onChange={set("expected_users")} data-testid="estimate-expected-users" placeholder="e.g. 50 staff, 5,000 customers" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            </div>
            <Field label="KEY FEATURES" testid="estimate-field-features"><Textarea rows={3} value={form.key_features} onChange={set("key_features")} data-testid="estimate-features" placeholder="Login, dashboards, reports, notifications…" className="bg-navy-900/60 border-white/10" /></Field>
            <Field label="INTEGRATIONS" testid="estimate-field-integrations"><Input value={form.integrations} onChange={set("integrations")} data-testid="estimate-integrations" placeholder="Payment gateway, CRM, WhatsApp API…" className="bg-navy-900/60 border-white/10 h-12" /></Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="PREFERRED DELIVERY" testid="estimate-field-delivery">
              <Select value={form.delivery_preference} onValueChange={set("delivery_preference")}>
                <SelectTrigger data-testid="estimate-delivery" className="bg-navy-900/60 border-white/10 h-12"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{DELIVERY.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="PREFERRED MODE" testid="estimate-field-mode">
              <Select value={form.mode_preference} onValueChange={set("mode_preference")}>
                <SelectTrigger data-testid="estimate-mode" className="bg-navy-900/60 border-white/10 h-12"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{MODE.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="EXPECTED TIMELINE" testid="estimate-field-timeline"><Input value={form.expected_timeline} onChange={set("expected_timeline")} data-testid="estimate-timeline" placeholder="e.g. this week / this month" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="ESTIMATED BUDGET" testid="estimate-field-budget">
              <Select value={form.estimated_budget} onValueChange={set("estimated_budget")}>
                <SelectTrigger data-testid="estimate-budget" className="bg-navy-900/60 border-white/10 h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-wider text-slate-400 mb-3">REQUIREMENT DOCUMENTS (PDF, images, docs — max 15MB each)</label>
            <input ref={fileRef} type="file" multiple onChange={onFiles} className="hidden" data-testid="estimate-file-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.doc,.docx,.zip" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="estimate-upload-btn" disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-white/25 px-6 py-3 text-xs font-semibold text-slate-300 hover:border-flame hover:text-flame transition-colors">
              <Paperclip className="w-4 h-4" /> {uploading ? "UPLOADING…" : "ATTACH FILES"}
            </button>
            {files.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="estimate-uploaded-files">
                {files.map((f) => (
                  <span key={f.file_id} className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-[11px] text-emerald-300">
                    {f.original_filename}
                    <button type="button" onClick={() => setFiles(files.filter((x) => x.file_id !== f.file_id))} data-testid={`estimate-remove-file-${f.file_id}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy || uploading} data-testid="estimate-submit-btn" className="w-full bg-flame hover:bg-flame-hover text-white font-heading font-bold h-14 rounded-full text-base">
            {busy ? "SUBMITTING…" : "GET MY ESTIMATE"} <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-center text-[11px] text-slate-600">
            Prefer chat? WhatsApp +{settings.whatsapp_number} — "Send your requirement."
          </p>
        </form>
      </div>
    </div>
  );
}
